import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

function formatGlCode(diff) {
  const code =
    diff.coaCode || diff.sourcePlacement?.coaCode || diff.targetPlacement?.coaCode;
  if (code) return code;
  if (diff.coaId != null) return String(diff.coaId);
  return "—";
}

function formatGlName(diff) {
  const name =
    diff.coaName || diff.sourcePlacement?.coaName || diff.targetPlacement?.coaName;
  return name || "—";
}

function resolveDepartmentName(placement) {
  if (!placement) return "—";
  if (placement.subDepartmentName) return placement.subDepartmentName;
  if (placement.departmentName) return placement.departmentName;
  if (placement.departmentMappingId != null) {
    return `Sub-department ${placement.departmentMappingId}`;
  }
  if (placement.departmentId != null) return `Department ${placement.departmentId}`;
  return "—";
}

function resolveAccountName(placement) {
  if (!placement) return "—";
  if (placement.structureCodeName) return placement.structureCodeName;
  if (placement.ccmsStructureCodeId != null) {
    return `Account ${placement.ccmsStructureCodeId}`;
  }
  return "—";
}

function formatPlacementText(placement) {
  if (!placement) return "—";
  return `Department: ${resolveDepartmentName(placement)}, Account: ${resolveAccountName(placement)}`;
}

function PlacementCellContent({ placement }) {
  if (!placement) return "—";
  const labelSx = { fontWeight: 700 };
  return (
    <Box component="span" sx={{ display: "block" }}>
      <Box component="span" sx={{ display: "block" }}>
        <Box component="span" sx={labelSx}>Department:</Box> {resolveDepartmentName(placement)}
      </Box>
      <Box component="span" sx={{ display: "block" }}>
        <Box component="span" sx={labelSx}>Account:</Box> {resolveAccountName(placement)}
      </Box>
    </Box>
  );
}

function ChangedAttributesCellContent({ changedAttributes, side }) {
  const labelSx = { fontWeight: 700 };
  return (
    <Box component="span" sx={{ display: "block" }}>
      {changedAttributes.map((attr) => (
        <Box key={attr.attribute} component="span" sx={{ display: "block" }}>
          <Box component="span" sx={labelSx}>{attr.label}:</Box>{" "}
          {(side === "source" ? attr.sourceValue : attr.targetValue) || "—"}
        </Box>
      ))}
    </Box>
  );
}

function SourceTargetCellContent({ diff, side }) {
  if (
    diff.differenceType === "ATTRIBUTE_MISMATCH" &&
    diff.changedAttributes &&
    diff.changedAttributes.length > 0
  ) {
    return (
      <ChangedAttributesCellContent
        changedAttributes={diff.changedAttributes}
        side={side}
      />
    );
  }
  const placement = side === "source" ? diff.sourcePlacement : diff.targetPlacement;
  return <PlacementCellContent placement={placement} />;
}

function getPlacementDifferences(source, target) {
  const diffs = [];
  const srcDept = resolveDepartmentName(source);
  const tgtDept = resolveDepartmentName(target);
  if (srcDept !== tgtDept) {
    diffs.push({ label: "Department", source: srcDept, target: tgtDept });
  }
  const srcAcct = resolveAccountName(source);
  const tgtAcct = resolveAccountName(target);
  if (srcAcct !== tgtAcct) {
    diffs.push({ label: "Account", source: srcAcct, target: tgtAcct });
  }
  return diffs;
}

function formatGlLabel(diff) {
  const glCode = formatGlCode(diff);
  const glName = formatGlName(diff);
  return glName !== "—" ? `${glCode} - ${glName}` : glCode;
}

function formatDetailsMessage(diff) {
  const glLabel = formatGlLabel(diff);
  const sourceLoc = formatPlacementText(diff.sourcePlacement);
  const targetLoc = formatPlacementText(diff.targetPlacement);

  switch (diff.differenceType) {
    case "ONLY_ON_SOURCE":
      return `This line is on the source plan at ${sourceLoc} but has no matching line on the target plan. It will not be copied.`;
    case "ONLY_ON_TARGET":
      return `This line is on the target plan at ${targetLoc} but not on the source plan. If you continue, this line item will remain unchanged.`;
    case "MULTIPLE_PLACEMENTS_ON_SOURCE":
      return `${glLabel} appears in more than one location on the source plan. Review source placements before copying.`;
    case "MULTIPLE_PLACEMENTS_ON_TARGET":
      return `${glLabel} appears in more than one location on the target plan. Review target placements before copying.`;
    default:
      return diff.message || "—";
  }
}

function CopyActionNote() {
  const bold = (text) => (
    <Box component="span" sx={{ fontWeight: 700 }}>
      {text}
    </Box>
  );

  return (
    <Typography sx={{ fontSize: 12, color: "#637381", mb: 2, lineHeight: 1.5 }}>
      When {bold("Department")} or {bold("Account")} on the source plan differs from the target
      plan, the data is still copied from source to the existing target line. Target{" "}
      {bold("Department")} and {bold("Account")} placement remain unchanged on the target plan.
    </Typography>
  );
}

function DetailsCellContent({ diff }) {
  if (diff.differenceType === "PLACEMENT_MISMATCH") {
    const glLabel = formatGlLabel(diff);
    const placementDiffs = getPlacementDifferences(
      diff.sourcePlacement,
      diff.targetPlacement
    );

    return (
      <Box sx={{ fontSize: 12, color: "#637381", lineHeight: 1.45 }}>
        <Box component="span" sx={{ display: "block", mb: 0.25 }}>
          {glLabel} is on both plans under different locations.
        </Box>
        {placementDiffs.map((d) => (
          <Box key={d.label} component="span" sx={{ display: "block", mb: 0.25 }}>
            Difference:{" "}
            <Box component="span" sx={{ fontWeight: 700 }}>
              {d.label}
            </Box>
            {": Source: "}
            {d.source}
            {" Target: "}
            {d.target}
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Box component="span" sx={{ fontSize: 12, color: "#637381", lineHeight: 1.45 }}>
      {formatDetailsMessage(diff)}
    </Box>
  );
}

function isRowSelectable(diff) {
  return diff.selectable !== false
    && diff.differenceType !== "ONLY_ON_SOURCE"
    && diff.differenceType !== "ONLY_ON_TARGET";
}

function DifferenceTable({ differences, selectedCoaIds, onToggleRow, onToggleAll, informational = false }) {
  if (!differences?.length) {
    return null;
  }

  // Copy-all mode: rows are display-only. Everything is copied regardless, so no checkboxes.
  const selectableRows = informational ? [] : differences.filter(isRowSelectable);
  const allSelected =
    selectableRows.length > 0 &&
    selectableRows.every((d) => selectedCoaIds.has(d.coaId));
  const someSelected =
    !allSelected && selectableRows.some((d) => selectedCoaIds.has(d.coaId));

  const cellSx = { fontSize: 12, verticalAlign: "middle" };

  return (
    <TableContainer sx={{ maxHeight: 360, border: "1px solid #E5E8EB", borderRadius: 1 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox" sx={{ bgcolor: "#F4F6F8", verticalAlign: "middle" }}>
              {!informational && (
                <Tooltip
                  title={
                    selectableRows.length === 0
                      ? "No selectable rows"
                      : allSelected
                      ? "Deselect all"
                      : "Select all"
                  }
                >
                  <span>
                    <Checkbox
                      size="small"
                      checked={allSelected}
                      indeterminate={someSelected}
                      disabled={selectableRows.length === 0}
                      onChange={() => onToggleAll(differences)}
                    />
                  </span>
                </Tooltip>
              )}
            </TableCell>
            <TableCell sx={{ fontWeight: 700, bgcolor: "#F4F6F8", whiteSpace: "nowrap", width: "10%" }}>
              GL Code
            </TableCell>
            <TableCell sx={{ fontWeight: 700, bgcolor: "#F4F6F8", minWidth: 180, width: "22%" }}>
              GL Code name
            </TableCell>
            <TableCell sx={{ fontWeight: 700, bgcolor: "#F4F6F8", width: "18%" }}>Source</TableCell>
            <TableCell sx={{ fontWeight: 700, bgcolor: "#F4F6F8", width: "18%" }}>Target</TableCell>
            <TableCell sx={{ fontWeight: 700, bgcolor: "#F4F6F8", maxWidth: 260, width: "26%" }}>
              Details
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {differences.map((diff, idx) => {
            const selectable = !informational && isRowSelectable(diff);
            const checked = selectable && selectedCoaIds.has(diff.coaId);
            return (
              <TableRow
                key={`${diff.differenceType}-${diff.coaId}-${idx}`}
                hover
                sx={selectable ? { cursor: "pointer" } : {}}
                onClick={selectable ? () => onToggleRow(diff.coaId) : undefined}
              >
                <TableCell padding="checkbox" sx={cellSx}>
                  {selectable ? (
                    <Tooltip
                      title={
                        checked
                          ? "Uncheck to skip this line during copy"
                          : "Check to include this line in copy"
                      }
                    >
                      <span>
                        <Checkbox
                          size="small"
                          checked={checked}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => onToggleRow(diff.coaId)}
                        />
                      </span>
                    </Tooltip>
                  ) : null}
                </TableCell>
                <TableCell sx={{ ...cellSx, whiteSpace: "nowrap" }}>
                  {formatGlCode(diff)}
                </TableCell>
                <TableCell sx={cellSx}>{formatGlName(diff)}</TableCell>
                <TableCell sx={cellSx}>
                  <SourceTargetCellContent diff={diff} side="source" />
                </TableCell>
                <TableCell sx={cellSx}>
                  <SourceTargetCellContent diff={diff} side="target" />
                </TableCell>
                <TableCell sx={{ ...cellSx, color: "#637381" }}>
                  <DetailsCellContent diff={diff} />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/**
 * Collects distinct coaIds of all selectable rows across a list of differences.
 */
function collectSelectableCoaIds(differences) {
  const ids = new Set();
  if (!differences) return ids;
  for (const diff of differences) {
    if (isRowSelectable(diff) && diff.coaId != null) {
      ids.add(diff.coaId);
    }
  }
  return ids;
}

function hasPlacementMismatchRows(differences) {
  return (differences || []).some((d) => d.differenceType === "PLACEMENT_MISMATCH");
}

const ImportPlanCopyValidationModal = ({
  open,
  onClose,
  onContinue,
  validationResult,
  validating = false,
  copyAllMode = false,
}) => {
  const byTarget = validationResult?.byTargetPlan || [];
  const showGrouped = byTarget.length > 1;

  // All differences — used to derive initial selection state
  const allDifferences = useMemo(
    () => validationResult?.differences || [],
    [validationResult]
  );

  // selectedCoaIds: coaIds the user wants to INCLUDE in copy (eligible rows only)
  const [selectedCoaIds, setSelectedCoaIds] = useState(() =>
    collectSelectableCoaIds(allDifferences)
  );

  // Reset selection whenever the validation result changes (modal reopened with new data)
  useEffect(() => {
    setSelectedCoaIds(collectSelectableCoaIds(allDifferences));
  }, [allDifferences]);

  const handleToggleRow = (coaId) => {
    setSelectedCoaIds((prev) => {
      const next = new Set(prev);
      if (next.has(coaId)) {
        next.delete(coaId);
      } else {
        next.add(coaId);
      }
      return next;
    });
  };

  const handleToggleAll = (differences) => {
    const selectable = differences.filter(isRowSelectable);
    const allChecked = selectable.every((d) => selectedCoaIds.has(d.coaId));
    setSelectedCoaIds((prev) => {
      const next = new Set(prev);
      if (allChecked) {
        selectable.forEach((d) => next.delete(d.coaId));
      } else {
        selectable.forEach((d) => { if (d.coaId != null) next.add(d.coaId); });
      }
      return next;
    });
  };

  const handleContinue = () => {
    // Copy-all: validation is display-only — copy everything. Lines present only on source have no
    // matching target row and are skipped by the backend, so no exclusions are needed here.
    if (copyAllMode) {
      onContinue([], []);
      return;
    }
    const allSelectableCoaIds = collectSelectableCoaIds(allDifferences);
    // Unchecked selectable rows are skipped during copy
    const excludedSelectableCoaIds = [...allSelectableCoaIds].filter((id) => !selectedCoaIds.has(id));
    // Rows without a checkbox (selectable === false), if any, are always excluded.
    const blockedCoaIds = (allDifferences || [])
      .filter((d) => d.selectable === false && d.coaId != null)
      .map((d) => d.coaId);
    const excludedCoaIds = [...new Set([...excludedSelectableCoaIds, ...blockedCoaIds])];
    // Checked rows are copied directly (driver/config preserved) and recalculated on the target plan.
    // They are NOT forced to input-by-month, so includedCoaIds stays empty.
    onContinue(excludedCoaIds, []);
  };

  const tableProps = {
    selectedCoaIds,
    onToggleRow: handleToggleRow,
    onToggleAll: handleToggleAll,
    informational: copyAllMode,
  };
  const showCopyActionNote = !copyAllMode && hasPlacementMismatchRows(allDifferences);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          pb: 1,
        }}
      >
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
          <WarningAmberIcon sx={{ color: "#FFAB00", mt: 0.25 }} />
          <Box>
            <Typography variant="h6" sx={{ fontSize: 18, fontWeight: 700, color: "#212B36" }}>
              Line item differences found
            </Typography>
          </Box>
        </Box>
        <IconButton aria-label="close" onClick={onClose} size="small">
          <ClearIcon sx={{ width: 16, height: 16 }} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        <Alert severity="warning" sx={{ mb: 2, alignItems: "flex-start" }}>
          {copyAllMode
            ? "Some lines differ on department or account between the source and target plans. All line items will still be copied from source to target — the target keeps its own department and account. Lines that exist only on the source are not copied."
            : "Some lines have a different driver, or sit under a different department/account, between the source and target plans. They will still be copied — the target keeps its own department and account. Uncheck any line you don't want to copy."}
        </Alert>
        {showCopyActionNote && <CopyActionNote />}
        {showGrouped ? (
          byTarget.map((target) => (
            <Box key={target.targetPlanId} sx={{ mb: 3 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#212B36", mb: 1 }}>
                Target: {target.targetPlanName || `Plan ${target.targetPlanId}`}
                {target.differences?.length != null ? ` (${target.differences.length})` : ""}
              </Typography>
              <DifferenceTable differences={target.differences} {...tableProps} />
            </Box>
          ))
        ) : (
          <DifferenceTable differences={validationResult?.differences} {...tableProps} />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={onClose} variant="outlined" disabled={validating}>
          Cancel
        </Button>
        <Button
          onClick={handleContinue}
          variant="contained"
          color="warning"
          disabled={validating}
          sx={{
            bgcolor: "#FFAB00",
            color: "#212B36",
            fontWeight: 700,
            "&:hover": { bgcolor: "#F59E0B" },
          }}
        >
          Continue copy
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportPlanCopyValidationModal;
